import { FC, useState, useCallback, useEffect, ChangeEvent } from "react";
import { connectRefinementList } from "react-instantsearch-dom";
import { RefinementListProvided } from "react-instantsearch-core";
import { Trans, t } from "@lingui/macro";
import { InputTextSearch } from "src/molecules/InputTextSearch";
import { Box } from "@otrium/core";
import { Checkbox } from "src/atoms/Checkbox";
import { HitType } from "src/types/Algolia";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import { union, orderBy } from "lodash-es";
import { BRAND_FACET } from "src/utils/algolia";
import { useLingui } from "@lingui/react";

interface Props extends RefinementListProvided {
  items: HitType[];
  refine: (str: string[]) => void;
  selectedFacet: string;
}

const BrandFilterComponent: FC<Props> = ({ items, refine, selectedFacet }) => {
  const { i18n } = useLingui();
  const [search, setSearch] = useState<string>("");
  const handleClearSearch = useCallback(() => {
    setSearch("");
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
    [search, setSearch]
  );

  const { data: favBrandsData } = useWishListBrands(9999, 0);

  useEffect(() => {
    if (selectedFacet !== BRAND_FACET) {
      setSearch("");
    }
  }, [selectedFacet]);

  if (!items || !items.length) {
    return null;
  }

  const filterBrands = (brand: HitType) => {
    const label = brand.label.split("||");
    const brandName = label[1];

    if (search.length) {
      return (
        brandName.toLowerCase().startsWith(search) ||
        brandName.toLowerCase().includes(search)
      );
    }
    return brand;
  };

  const sortedBrands = orderBy(items, "count", "desc");
  const selectedBrands = items
    .filter((brand) => {
      return brand.isRefined;
    })
    .map((filteredBrand) => {
      return filteredBrand.label;
    });

  const favBrands = favBrandsData?.wishlistBrands?.items.map((brand) => {
    return brand?.id;
  });
  const favBrandsIds = items
    .filter((brand) => {
      const brandId = brand.label.split("||")[0];
      return favBrands?.includes(brandId);
    })
    .map((mapedBrand) => {
      return mapedBrand.label;
    });
  const toggleFavBrands = () => {
    const isFavBrandsChecked = favBrandsIds.every((selectedBrand) =>
      selectedBrands.includes(selectedBrand)
    );

    if (!isFavBrandsChecked) {
      refine(union(selectedBrands, favBrandsIds));
    } else {
      const brandIds = selectedBrands.filter((brand) => {
        return !favBrandsIds.includes(brand);
      });
      refine(brandIds);
    }
  };

  return (
    <Box data-testid="filter-brand-list">
      {selectedFacet === BRAND_FACET && (
        <Box p={3}>
          <InputTextSearch
            placeholder={t(i18n)`Search for brand`}
            handleChange={handleChange}
            onClearSearch={handleClearSearch}
          />
        </Box>
      )}
      {favBrandsIds.length ? (
        <Box bg="tone.sand" py={[4, 4, 3]} pl={3} ml={[0, 0, 0, "-16px"]}>
          <Checkbox
            checked={favBrandsIds.every((selectedBrand) =>
              selectedBrands.includes(selectedBrand)
            )}
            onChange={toggleFavBrands}
          >
            <span data-testid="favourite-brands-toggle-span">
              <Trans>Select my favourite brands</Trans>
            </span>
          </Checkbox>
        </Box>
      ) : null}
      <Box>
        <Box
          as="ul"
          sx={{
            listStyle: "none",
          }}
        >
          {sortedBrands?.filter(filterBrands).map((item, i) => {
            const label = item.label.split("||");
            const labelName = label[1];
            return (
              <Box as="li" key={`size-filter-item-${i}`} p={3}>
                <Checkbox
                  checked={item.isRefined}
                  onChange={() => refine(item.value)}
                >
                  {labelName}
                </Checkbox>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const BrandFilter = connectRefinementList(BrandFilterComponent);

export default BrandFilter;
